/**
 * @theme/product-modal — extends ModalDialog; lightbox for product gallery images.
 *
 * On show(opener), reads `data-media-id` from the opener and activates the matching media in the modal.
 * Auto-loads deferred YouTube/Vimeo embeds.
 */

import { defineComponent } from '@theme/component';
import { ModalDialog } from '@theme/modal-dialog';

/**
 * @extends {ModalDialog}
 */
export class ProductModal extends ModalDialog {
  /** @param {HTMLElement | null} [opener] */
  show(opener) {
    super.show(opener);
    this.#showActiveMedia();
  }

  hide() { super.hide(); }

  #showActiveMedia() {
    if (!this.openedBy) return;
    const targetId = this.openedBy.getAttribute('data-media-id');
    if (!targetId) return;

    this.querySelectorAll(`[data-media-id]:not([data-media-id="${targetId}"])`).forEach((el) => {
      el.classList.remove('active');
    });
    /** @type {HTMLElement | null} */
    const active = this.querySelector(`[data-media-id="${targetId}"]`);
    if (!active) return;
    active.classList.add('active');
    active.scrollIntoView();

    /** @type {HTMLElement | null} */
    const container = this.querySelector('[role="document"]');
    if (container) {
      container.scrollLeft = (active.clientWidth - container.clientWidth) / 2;
    }

    if (active.nodeName === 'DEFERRED-MEDIA') {
      const template = active.querySelector('template');
      if (template?.content?.querySelector('.js-youtube')) {
        /** @type {any} */ (active).loadContent?.();
      }
    }
  }
}

defineComponent('product-modal', ProductModal);
